/**
 * Modal para editar el perfil del usuario.
 * Permite modificar datos personales, bio, y características demográficas.
 * Usa hooks de TanStack Query para obtener y actualizar datos.
 * 
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Estado de apertura del modal
 * @param {function} props.onClose - Función para cerrar el modal
 * @param {boolean} props.isVisible - Si el modal es visible (para optimización)
 */

import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { useCurrentUserProfile } from '../../../hooks/useCurrentUserProfile';
import { useUpdateProfileMutation } from '../../../hooks/useProfileMutations';
import { useAuth } from '../../../hooks/useAuth';

export const ProfileModal = ({ isOpen, onClose, isVisible }) => {
  const { user } = useAuth();
  
  // Hook para obtener datos del usuario actual
  const { userProfile, isLoading: profileLoading } = useCurrentUserProfile();
  
  // Hook para mutación de perfil
  const { mutate: updateProfile, isPending: isUpdating } = useUpdateProfileMutation();

  // Estado local del formulario
  const [formData, setFormData] = useState({
    full_name: '',
    bio: '',
    years: '',
    altura: '',
    peso: '',
    sex: 0,
    sex_pref: 0,
    // Otros campos se agregarán según necesidad
  });

  // Estado para rastrear si hay cambios sin guardar
  const [isFormChanged, setIsFormChanged] = useState(false);

  // Efecto para cargar datos del perfil cuando se abre el modal
  useEffect(() => {
    if (isVisible && userProfile) {
      setFormData({
        full_name: userProfile.full_name || '',
        bio: userProfile.bio || '',
        years: userProfile.years || '',
        altura: userProfile.altura || '',
        peso: userProfile.peso || '',
        sex: userProfile.sex || 0,
        sex_pref: userProfile.sex_pref || 0,
      });
      setIsFormChanged(false);
    }
  }, [isVisible, userProfile]);

  // Maneja cambios en los campos del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setIsFormChanged(true);
  };

  // Maneja el guardado del perfil
  const handleSave = async () => {
    if (!isFormChanged) {
      onClose();
      return;
    }

    try {
      // Convertir campos numéricos
      const dataToSave = {
        ...formData,
        years: parseInt(formData.years) || 0,
        altura: parseInt(formData.altura) || 0,
        peso: parseInt(formData.peso) || 0,
        sex: parseInt(formData.sex) || 0,
        sex_pref: parseInt(formData.sex_pref) || 0,
      };

      await updateProfile(dataToSave);
      setIsFormChanged(false);
      onClose();
    } catch (error) {
      console.error('[ProfileModal] Error guardando perfil:', error);
    }
  };

  // Maneja el cierre del modal con confirmación si hay cambios
  const handleClose = () => {
    if (isFormChanged) {
      const confirmed = window.confirm('Tienes cambios sin guardar. ¿Deseas cerrar sin guardar?');
      if (!confirmed) return;
    }
    onClose();
  };

  // Si el modal no está abierto, no renderizar nada
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div 
        className="bg-neutral-900 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg"
        style={{ backgroundColor: '#2A183D' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-900">
          <h2 className="text-white text-lg">EeeeeeeeeSditar Perfil</h2>
          <button 
            onClick={handleClose}
            className="p-2 hover:bg-neutral-900 rounded-lg transition-colors"
          >
            <FiX size={24} className="text-white" />
          </button>
        </div>

        {/* Formulario */}
        <div className="p-4 space-y-4">
          {profileLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-white">Cargando perfil...</span>
            </div>
          ) : (
            <>
              {/* Nombre de pantalla */}
              <div>
                <label className="text-white block mb-2">Nombre de pantalla</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  maxLength={16}
                  className="w-full bg-neutral-900 border-2 border-neutral-900 rounded-lg px-4 py-3 text-white"
                  style={{ backgroundColor: '#3C2B4F', borderColor: '#4A3B5F' }}
                  placeholder="Tu nombre"
                />
                <span className="text-gray-400 text-xs mt-1">
                  {formData.full_name.length}/16 caracteres
                </span>
              </div>

              {/* Bio */}
              <div>
                <label className="text-white block mb-2">Acerca de mí</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  maxLength={250}
                  rows={4}
                  className="w-full bg-neutral-900 border-2 border-neutral-900 rounded-lg px-4 py-3 text-white resize-none"
                  style={{ backgroundColor: '#3C2B4F', borderColor: '#4A3B5F' }}
                  placeholder="Cuéntanos sobre ti..."
                />
                <span className="text-gray-400 text-xs mt-1">
                  {formData.bio.length}/250 caracteres
                </span>
              </div>

              {/* Edad */}
              <div>
                <label className="text-white block mb-2">Edad</label>
                <input
                  type="number"
                  name="years"
                  value={formData.years}
                  onChange={handleInputChange}
                  min={18}
                  max={99}
                  className="w-full bg-neutral-900 border-2 border-neutral-900 rounded-lg px-4 py-3 text-white"
                  style={{ backgroundColor: '#3C2B4F', borderColor: '#4A3B5F' }}
                  placeholder="Tu edad"
                />
              </div>

              {/* Altura (cm) */}
              <div>
                <label className="text-white block mb-2">Altura (cm)</label>
                <input
                  type="number"
                  name="altura"
                  value={formData.altura}
                  onChange={handleInputChange}
                  min={100}
                  max={250}
                  className="w-full bg-neutral-900 border-2 border-neutral-900 rounded-lg px-4 py-3 text-white"
                  style={{ backgroundColor: '#3C2B4F', borderColor: '#4A3B5F' }}
                  placeholder="Tu altura en centímetros"
                />
              </div>

              {/* Peso (kg) */}
              <div>
                <label className="text-white block mb-2">Peso (kg)</label>
                <input
                  type="number"
                  name="peso"
                  value={formData.peso}
                  onChange={handleInputChange}
                  min={30}
                  max={200}
                  className="w-full bg-neutral-900 border-2 border-neutral-900 rounded-lg px-4 py-3 text-white"
                  style={{ backgroundColor: '#3C2B4F', borderColor: '#4A3B5F' }}
                  placeholder="Tu peso en kilogramos"
                />
              </div>

              {/* Género */}
              <div>
                <label className="text-white block mb-2">Género</label>
                <select
                  name="sex"
                  value={formData.sex}
                  onChange={handleInputChange}
                  className="w-full bg-neutral-900 border-2 border-neutral-900 rounded-lg px-4 py-3 text-white"
                  style={{ backgroundColor: '#3C2B4F', borderColor: '#4A3B5F' }}
                >
                  <option value={0}>No especificado</option>
                  <option value={1}>Mujer</option>
                  <option value={2}>Hombre</option>
                  <option value={3}>No binario</option>
                </select>
              </div>

              {/* Preferencia de género */}
              <div>
                <label className="text-white block mb-2">Busco</label>
                <select
                  name="sex_pref"
                  value={formData.sex_pref}
                  onChange={handleInputChange}
                  className="w-full bg-neutral-900 border-2 border-neutral-900 rounded-lg px-4 py-3 text-white"
                  style={{ backgroundColor: '#3C2B4F', borderColor: '#4A3B5F' }}
                >
                  <option value={0}>Ambos</option>
                  <option value={1}>Mujeres</option>
                  <option value={2}>Hombres</option>
                </select>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-neutral-900">
          <button
            onClick={handleSave}
            disabled={!isFormChanged || isUpdating}
            className="w-full py-3 bg-primary rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80 transition-opacity"
            style={{ backgroundColor: '#DA0037' }}
          >
            {isUpdating ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};
